import axios from "axios";
import env from "../../variables/config";
import { ICompany, CompanyFormData } from "../company";

axios.defaults.baseURL = env.apiUrl;

const config = () => ({
    headers: { Authorization: "Bearer " + localStorage.getItem("token") }
});

export interface AdminCompanyFilters {
    status?: string;
    industry?: string;
    size?: string;
    fundingStage?: string;
    search?: string;
    tags?: string[];
    sortBy?: string;
    order?: string;
    page?: number;
    limit?: number;
}

export interface CompanyStats {
    totalCompanies: number;
    recentCompanies: ICompany[];
    byStatus: { status: string; count: number }[];
    byIndustry: { industry: string; count: number }[];
    byFundingStage: { fundingStage: string; count: number }[];
    bySize: { size: string; count: number }[];
}

export interface FilterValues {
    industries?: string[];
    statuses?: string[];
    sizes?: string[];
    fundingStages?: string[];
    tags?: string[];
}

// Get all companies (admin view)
export const getAdminCompanies = async (filters?: AdminCompanyFilters) => {
    const params = new URLSearchParams();
    if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                if (Array.isArray(value)) {
                    value.forEach(v => params.append(key, v));
                } else {
                    params.append(key, value.toString());
                }
            }
        });
    }
    const res = await axios.get(`/admin/companies?${params.toString()}`, config());
    return res.data;
};

// Get a specific company (admin)
export const getAdminCompany = async (id: string): Promise<ICompany> => {
    const res = await axios.get(`/admin/companies/${id}`, config());
    return res.data;
};

// Get company by SIREN (admin)
export const getAdminCompanyBySiren = async (siren: string): Promise<ICompany> => {
    const res = await axios.get(`/admin/companies/siren/${siren}`, config());
    return res.data;
};

// Create a new company (admin)
export const createAdminCompany = async (data: CompanyFormData): Promise<ICompany> => {
    const res = await axios.post('/admin/companies', data, config());
    return res.data;
};

// Update a company (admin)
export const updateAdminCompany = async (id: string, data: CompanyFormData): Promise<ICompany> => {
    const res = await axios.put(`/admin/companies/${id}`, data, config());
    return res.data;
};

// Delete a company (admin)
export const deleteAdminCompany = async (id: string) => {
    const res = await axios.delete(`/admin/companies/${id}`, config());
    return res.data;
};

// Batch create companies (admin)
export const batchCreateCompanies = async (companies: CompanyFormData[]) => {
    const res = await axios.post('/admin/companies/batch', { companies }, config());
    return res.data;
};

// Get admin stats
export const getAdminCompanyStats = async (): Promise<CompanyStats> => {
    const res = await axios.get('/admin/companies/stats/admin', config());
    return res.data;
};

// Get filter values
export const getCompanyFilterValues = async (field: string): Promise<string[]> => {
    const res = await axios.get(`/admin/companies/filters/values?field=${field}`, config());
    return res.data;
};

// Get all filter values at once
export const getAllCompanyFilterValues = async (): Promise<FilterValues> => {
    const [industries, statuses, sizes, fundingStages, tags] = await Promise.all([
        getCompanyFilterValues('industries'),
        getCompanyFilterValues('statuses'),
        getCompanyFilterValues('sizes'),
        getCompanyFilterValues('fundingStages'),
        getCompanyFilterValues('tags'),
    ]);
    
    return {
        industries,
        statuses,
        sizes,
        fundingStages,
        tags,
    };
};